import React, { useContext, useEffect, useRef } from "react";
import { useAuthStore } from "../../@shared/store/useAuthStore";
import SpriteAnimator from "./SpriteAnimator";
import {
  motion,
  useAnimation,
  useMotionValue,
  useTransform,
} from "framer-motion";
import { Box, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import AudioManager from "../../@shared/utils/AudioManager";
import { useTutorial } from "../../@shared/utils/useTutorial";
import { triggerDriverStep } from "../../@shared/utils/useDriverTutorial";
import { MainContext } from "../../App";
import { parseCardEffect } from "../../@shared/game/deckEngine";
import { getColorShades } from "../../@shared/utils/Utils";
import CharacterCard from "../CharacterCard";

import fire from "../../@shared/assets/fire.png";
import ice from "../../@shared/assets/ice.png";
import void1 from "../../@shared/assets/void.png";
import wind from "../../@shared/assets/wind.png";
import water from "../../@shared/assets/icons/water.png";
import grass from "../../@shared/assets/icons/grass.png";
import rock from "../../@shared/assets/icons/rock.png";
import electric from "../../@shared/assets/icons/electric.png";
import { Cancel } from "@mui/icons-material";

const elementIconMap = {
  fire,
  water,
  grass,
  rock,
  air: wind,
  electric,
  void: void1,
  ice,
};

const CLOAK_IMAGE_URL =
  "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/Infinity%20Card%20Images%2FCloak%20of%20Nullification.png?alt=media&token=13d542d6-99fa-4cfd-9a97-48e50920d1ef";
const MAGIC_BARRIER_IMAGE_URL =
  "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/Infinity%20Card%20Images%2FMagic%20Barrier.png?alt=media&token=ad90f016-2060-42d6-ba7d-8d1685114529";

const MotionBox = motion(Box);
const MotionDiv = motion.div;

const TEAM_COLORS = {
  red: "#ff4d4d",
  blue: "#4da6ff",
  green: "#3ccf3c",
};

const PlayerPlatform = ({
  id,
  name,
  character,
  isSelected,
  onSelect,
  matchPhase,
  enterIndex = 0,
  playerTurn,
  isMyTurn,
  isBot,
  activeAnimation,
  nextTurn,
  cpMovesLeft,
  playerMoveType,
  lastMove,
  isTutorial,
  coOp,
  away,
  playerEmote,
  team,
  currentPlayerTeam,
  selectedCard,
}) => {
  const { userData } = useAuthStore();
  const { deviceType, Theme } = useContext(MainContext);
  const isMobile = deviceType === "mobile";
  const { progress, sequence, advanceStep } = useTutorial(userData?.id);
  const isCurrentPlayer = id === userData?.id;

  const teamColor = team ? TEAM_COLORS[team] : null;
  const isTeammate =
    !!teamColor && !!currentPlayerTeam && team === currentPlayerTeam;

  const teamShades = team ? getColorShades(teamColor) : null;

  const [incomingCardImg, setIncomingCardImg] = useState(null);
  const [showPowerUp, setShowPowerUp] = useState({
    on: false,
    color: "#fff",
    direction: "up",
  });

  const animationCooldownRef = useRef(false);
  const [animationName, setAnimationName] = useState(null); // 💡 Local state for sprite animationName
  const spriteRef = useRef();
  const lastProcessedMoveRef = useRef(null);

  const bounce = useMotionValue(0);
  // const blinkControls = useAnimation();
  const containerControls = useAnimation();
  const platformControls = useAnimation();

  const controls = useAnimation();

  const shadowScale = useTransform(bounce, [0, -5], [1, 0.875]);

  const stanceCardControls = useAnimation();
  const stanceIconControls = useAnimation();
  const lastStanceRef = useRef(character?.stance);

  const tilt = character?.isDead
    ? "rotateX(60deg)"
    : playerTurn || isSelected
      ? "rotateX(0deg)"
      : "rotateX(30deg)";

  const bounceVariants = {
    idle: { scale: 1, opacity: 0.96 },
    selected: {
      scale: [1, 1.1, 1],
      opacity: [0.92, 1, 0.92],
      transition: {
        duration: 0.75,
        repeat: Infinity,
        repeatType: "loop",
        ease: "easeInOut",
      },
    },
  };

  const handleShake = (num = 3) => {
    bounce.set(-5);
    platformControls.stop();
    platformControls.start({
      x: [0, -10, 10, -8, 8, 0],
      transition: {
        duration: 0.1,
        repeat: num,
        ease: "easeInOut",
      },
    });
  };

  const handleTutorialBattleAdvance = () => {
    if (progress?.currentStepIndex === 0 && character?.name === "Luna") {
      advanceStep();
    }
  };

  // src/@shared/game/deckEngine.js
  // existing export: parseCardEffect(card) { ... }

  const determinePowerUpColor = (card = {}) => {
    const firstArea = (card.areaAffected?.[0] || "").toLowerCase();

    // Map area → color
    const colorMap = {
      health: "#ff5252", // red
      accuracy: "#30d8fc", // blue
      defense: "#ffd54f", // yellow
      attack: "#ff9800", // orange
      "turn loss": "#9c27b0", // purple
      "player hand": "#9c27b0", // purple
      "player position": "#37b027ff", // purple
      "spin wheel": "#2735b0ff", // purple
    };

    // Choose color by first area; default to white
    // Special-case: anything that contains "turn" => treat as "turn loss" color
    const isTurnish = firstArea.includes("turn");
    const color = isTurnish
      ? colorMap["turn loss"]
      : colorMap[firstArea] || "#ffffff";

    // Use parseCardEffect to infer direction (aid = up, affliction/turn-loss = down)
    const { isAffliction, isAid } = parseCardEffect(card || {});
    const direction = isAffliction || isTurnish ? "down" : isAid ? "up" : "up";

    return { color, direction };
  };

  const elementImg = elementIconMap[character?.element] || null;

  const getElementBGColor = () => {
    switch (character?.element) {
      case "fire":
        return "rgb(255, 205, 130)";
      case "water":
        return "rgb(183, 216, 255)";
      case "grass":
        return "rgb(185, 255, 188)";
      case "rock":
        return "rgb(255, 234, 169)";
      case "air":
        return "rgb(209, 227, 235)";
      case "electric":
        return "rgb(255, 252, 173)";
      case "void":
        return "rgb(188, 154, 201)";
      case "ice":
        return "rgb(227, 246, 255)";
      default:
        return "#c1c1c1";
    }
  };

  const getElementColor = () => {
    switch (character?.element) {
      case "fire":
        return "rgba(255, 0, 0, 1)";
      case "water":
        return "rgba(0, 115, 255, 1)";
      case "grass":
        return "rgba(0, 255, 13, 1)";
      case "rock":
        return "rgba(255, 191, 0, 1)";
      case "air":
        return "rgba(0, 179, 255, 1)";
      case "electric":
        return "rgba(255, 247, 0, 1)";
      case "void":
        return "rgba(183, 0, 255, 1)";
      case "ice":
        return "rgba(0, 174, 255, 1)";
      default:
        return "#c1c1c1";
    }
  };

  const elementGBColor = getElementBGColor();

  const boosts = character?.boosts || {};
  const displayHealth =
    Number(character?.startingHealth ?? character?.health ?? 0) +
    Number(boosts?.health || 0);
  const displayAttack =
    Number(character?.startingAttack ?? character?.attack ?? 0) +
    Number(boosts?.attack || 0);
  const displayDefense =
    Number(character?.startingDefense ?? character?.defense ?? 0) +
    Number(boosts?.defense || 0);
  const displayAccuracy =
    Number(character?.startingAccuracy ?? character?.accuracy ?? 0) +
    Number(boosts?.accuracy || 0);

  // Top platform badges (outside CharacterCard) should reflect live/buffed combat stats.
  const totalHealth = Number(character?.health ?? 0);
  const totalAttack =
    Number(character?.attack ?? 0) +
    Number(
      character?.buffs?.attack?.reduce((acc, buff) => acc + Number(buff?.amt || 0), 0) ||
        0,
    );
  const totalDefense = Number(character?.defense ?? 0);
  const totalAccuracy =
    Number(character?.accuracy ?? 0) +
    Number(
      character?.buffs?.accuracy?.reduce(
        (acc, buff) => acc + Number(buff?.amt || 0),
        0,
      ) || 0,
    );
  const cloakedTurns = Number(character?.cloakedTurns ?? 0);
  const magicBarrierTurns = Number(character?.magicBarrierTurns ?? 0);
  const skippedTurns = Number(character?.skippedTurns ?? character?.turnLoss ?? 0);
  const statusIndicators = [
    cloakedTurns > 0
      ? {
          key: "cloak",
          count: cloakedTurns,
          label: "Cloak Of Nullification",
          description: "This player cannot be selected for attacks while the cloak is active.",
          kind: "image",
          src: CLOAK_IMAGE_URL,
        }
      : null,
    magicBarrierTurns > 0
      ? {
          key: "magic-barrier",
          count: magicBarrierTurns,
          label: "Magic Barrier",
          description:
            "Affliction cards have no effect on this player while the barrier is active.",
          kind: "image",
          src: MAGIC_BARRIER_IMAGE_URL,
        }
      : null,
    skippedTurns > 0
      ? {
          key: "skipped-turns",
          count: skippedTurns,
          label: "Skipped Turns",
          description: "This player will lose upcoming turns while this effect remains.",
          kind: "icon",
        }
      : null,
  ].filter(Boolean);

  // Keep buff/debuff visual FX disabled on platform badges.
  const isShownStatModified = false;
  const isShownStatBuffed = false;
  const isHealthBuffed = false;
  const isAccuracyBuffed = false;
  const isAccuracyDebuffed = false;

  useEffect(() => {
    if (matchPhase !== "started") return;

    containerControls.start({
      opacity: 1,
      y: 0,
      transition: { duration: 0.35, delay: enterIndex * 0.08, ease: "easeOut" },
    });
  }, [matchPhase, enterIndex, containerControls]);

  useEffect(() => {
    if (isSelected) {
      controls.start({
        y: [-5, 5],
        x: [-5, 5],
        skewX: ["50deg", "50deg"],
        transition: {
          duration: 0.4,
          repeat: Infinity,
          repeatType: "reverse",
        },
      });
    } else {
      controls.start({
        y: 10,
        x: 10,
        transition: { duration: 0.3 },
        skewX: "50deg",
      });
    }
  }, [isSelected]);

  //   🔥 Watch for animation triggers
  useEffect(() => {
    if (!lastMove) return;

    const { type, target, defender, timestamp, card } = lastMove;

    // ✅ Skip if we've already handled this move
    if (lastProcessedMoveRef.current === timestamp) return;
    lastProcessedMoveRef.current = timestamp;

    animationCooldownRef.current = timestamp;

    if (type === "attack" && (target === name || defender === name)) {
      setTimeout(() => {
        containerControls.start({
          opacity: [1, 0, 1],

          transition: {
            duration: 0.2,
            repeat: 10,
            ease: "easeInOut",
          },
        });
      }, 2000);
      console.log("🔥 ATTACK ANIMATION TRIGGERED");
    } else if (type === "playCard" && target === name && card?.imageUrl) {
      setIncomingCardImg(card.imageUrl);

      setTimeout(() => {
        AudioManager.playSFX("buff1");
      }, 1500);

      // 🔮 derive power-up style
      const { color: powerColor, direction } = determinePowerUpColor(card);

      // 👉 Trigger power-up near the tail end
      setTimeout(
        () => setShowPowerUp({ on: true, color: powerColor, direction }),
        2600,
      );
      // setTimeout(() => setShowPowerUp(false), 2600);
      setTimeout(() => {
        setIncomingCardImg(null);
        setShowPowerUp((prev) => ({ ...prev, on: false }));
      }, 4500);

      console.log("🎴 CARD PLAY ANIMATION TRIGGERED");
    }

    setTimeout(() => {
      animationCooldownRef.current = false;
    }, 3000);
  }, [lastMove, name]);

  useEffect(() => {
    if (matchPhase !== "started") return;
    if (!character || character?.isDead) return;

    const prev = lastStanceRef.current;
    const next = character?.stance;

    // Only animate when stance actually changes
    if (!prev || prev === next) return;

    lastStanceRef.current = next;

    const run = async () => {
      // 1) slight rise
      await stanceCardControls.start({
        y: -8,
        transition: { duration: 0.12, ease: "easeOut" },
      });

      // 2) quick full flip (front -> back -> front)
      await stanceCardControls.start({
        rotateY: [0, 180, 180],
        transition: { duration: 0.25, ease: "easeInOut" },
      });

      // 3) return to origin (and normalize rotate to avoid accumulating)
      await stanceCardControls.start({
        y: 0,
        rotateY: 0,
        transition: { duration: 0.12, ease: "easeOut" },
      });

      // 4) pulse the stance icon once
      await stanceIconControls.start({
        scale: [1, 1.25, 1],
        transition: { duration: 0.18, ease: "easeOut" },
      });
    };

    run();
  }, [
    character?.stance,
    character?.isDead,
    matchPhase,
    stanceCardControls,
    stanceIconControls,
    character,
  ]);

  // 🔼 Rising dash-line "power-up" overlay
  const PowerUpRisers = ({ isMobile, color = "#ffffff", direction = "up" }) => {
    // positions (px) relative to the platform center
    const lanes = [
      -18, -16, -14, -12, -10, -8, -6, -4, -2, 0, 2, 4, 6, 8, 10, 12, 14, 16,
      18,
    ];

    const startY = direction === "down" ? -36 : 22;
    const endY = direction === "down" ? 22 : -36;
    const gradDir = direction === "down" ? "to bottom" : "to top";

    return (
      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 100, // under character (z=99), above background
          overflow: "visible",
          color, // <- drives currentColor
        }}
      >
        {lanes.map((offset, idx) => {
          // pick random values for this instance
          const delay = Math.random() * 1.5; // random entry delay (0–1.5s)
          const duration = 0.3 + Math.random() * 0.6; // varied rise speed
          const repeatDelay = 0.2 + Math.random() * 1; // varied downtime between pulses

          return (
            <motion.div
              key={offset + "-" + idx}
              initial={{ y: startY, opacity: 0 }}
              animate={{ y: endY, opacity: [0, 1, 0] }}
              transition={{
                duration,
                delay,
                repeat: Infinity,
                repeatDelay,
                ease: "easeOut",
              }}
              style={{
                position: "absolute",
                left: `calc(50% + ${offset}px)`,
                bottom: "28%",
                width: 2,
                height: isMobile ? 24 : 30,
                borderRadius: 2,
                background: `linear-gradient(${gradDir},
                rgba(255,255,255,0.3) 0%,
                currentColor 60%,
                #ffffff 100%)`,
                boxShadow: "0 0 8px rgba(34, 255, 60, 0.45)",
                border: ".5px solid #00000050",
              }}
            />
          );
        })}
      </div>
    );
  };

  const baseBackground = isSelected
    ? "#ef169c"
    : playerTurn
      ? "#fbcf57"
      : coOp
        ? isBot
          ? "#0070f7"
          : "#4ad24a"
        : isCurrentPlayer
          ? "#30d8fc"
          : "#0070f7";

  const baseBorderColor = isSelected
    ? "#f96ed0"
    : playerTurn
      ? "transparent"
      : coOp
        ? isBot
          ? "#0070f7"
          : isCurrentPlayer
            ? "white"
            : "#003100"
        : isCurrentPlayer
          ? "#16116e"
          : "#30d8fc";

  const isTeamsMode = !!teamColor;

  // 🟦 Background: team color only when NOT selected and NOT current turn
  const containerBackground =
    isTeamsMode && !isSelected && !playerTurn
      ? teamShades?.main
      : baseBackground;

  // 🟨 Border: same rule – team border only when idle
  const borderColor =
    isTeamsMode && !isSelected && !playerTurn
      ? teamShades?.light
      : baseBorderColor;

  // 🟣 Shadow: team shadow only when idle; otherwise use base highlight colors
  const boxShadowColor =
    isTeamsMode && !isSelected && !playerTurn
      ? teamShades?.dark
      : isSelected
        ? "#b90c98"
        : playerTurn
          ? "#af8001"
          : coOp
            ? isBot
              ? "#16116e"
              : "#003100"
            : isCurrentPlayer
              ? "rgb(0, 150, 184)"
              : "#16116e";

  const isBlockedAttackTarget =
    playerMoveType === "attack" &&
    ((playerTurn && id === userData?.id) ||
      character?.isDead ||
      character?.cloakedTurns > 0 ||
      (coOp && !isBot) ||
      isTeammate);

  const selectedCardAreas = Array.isArray(selectedCard?.areaAffected)
    ? selectedCard.areaAffected.map((area) => String(area || "").toLowerCase())
    : [];
  const selectedCardTone =
    selectedCard?.specialty || selectedCardAreas.includes("health")
      ? {
          primary: "#ff5555",
          glow: "rgba(255, 85, 85, 0.6)",
          iconGlow: "drop-shadow(0 0 10px rgba(255, 140, 140, 0.95))",
        }
      : selectedCardAreas.includes("attack")
        ? {
            primary: "#ff9b3d",
            glow: "rgba(255, 155, 61, 0.58)",
            iconGlow: "drop-shadow(0 0 10px rgba(255, 188, 120, 0.92))",
          }
        : selectedCardAreas.includes("accuracy")
          ? {
              primary: "#4ed8ff",
              glow: "rgba(78, 216, 255, 0.55)",
              iconGlow: "drop-shadow(0 0 10px rgba(78, 216, 255, 0.9))",
            }
          : selectedCardAreas.includes("defense")
            ? {
                primary: "#ffd84a",
                glow: "rgba(255, 216, 74, 0.55)",
                iconGlow: "drop-shadow(0 0 10px rgba(255, 230, 132, 0.92))",
              }
            : {
                primary: "#ff5555",
                glow: "rgba(255, 85, 85, 0.6)",
                iconGlow: "drop-shadow(0 0 10px rgba(255, 140, 140, 0.95))",
              };

  const targetLockTone =
    playerMoveType === "attack"
      ? {
          primary: "#ff5555",
          glow: "rgba(255, 85, 85, 0.6)",
          iconGlow: "drop-shadow(0 0 10px rgba(255, 140, 140, 0.95))",
        }
      : selectedCardTone;

  const TargetLockOverlay = () => {
    const cardImageUrl = selectedCard?.imageUrl || null;
    const accentStroke = targetLockTone.primary;
    const accentGlow = targetLockTone.glow;
    const reticleFilter = `drop-shadow(0 0 10px ${accentGlow}) drop-shadow(0 0 18px ${accentGlow})`;

    return (
      <motion.div
        animate={{
          opacity: [0.82, 1, 0.82],
          scale: [1, 1.02, 1],
        }}
        transition={{
          duration: 0.85,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 140,
          pointerEvents: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: { xs: "8%", md: "10%" },
            borderRadius: "14px",
            background:
              "radial-gradient(circle at center, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 28%, transparent 62%)",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: { xs: 110, md: 136 },
            height: { xs: 110, md: 136 },
            transform: "translate(-50%, -50%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <motion.div
            animate={{ rotate: [0, 1.2, -1.2, 0], scale: [1, 1.025, 1] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              filter: reticleFilter,
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 160 160"
              sx={{
                width: "100%",
                height: "100%",
                overflow: "visible",
              }}
            >
              <g fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path
                  d="M26 48 V28 Q26 18 36 18 H56 M104 18 H124 Q134 18 134 28 V48 M26 112 V132 Q26 142 36 142 H56 M104 142 H124 Q134 142 134 132 V112"
                  stroke={accentStroke}
                  strokeWidth="10"
                />
                <path
                  d="M26 48 V28 Q26 18 36 18 H56 M104 18 H124 Q134 18 134 28 V48 M26 112 V132 Q26 142 36 142 H56 M104 142 H124 Q134 142 134 132 V112"
                  stroke="#fff"
                  strokeWidth="4.5"
                />

                <circle cx="80" cy="80" r="46" stroke={accentStroke} strokeWidth="10" />
                <circle cx="80" cy="80" r="46" stroke="#fff" strokeWidth="4.5" />

                <path d="M80 31 L80 39" stroke={accentStroke} strokeWidth="10" />
                <path d="M80 31 L80 39" stroke="#fff" strokeWidth="4.5" />
                <path d="M80 121 L80 129" stroke={accentStroke} strokeWidth="10" />
                <path d="M80 121 L80 129" stroke="#fff" strokeWidth="4.5" />
                <path d="M31 80 L39 80" stroke={accentStroke} strokeWidth="10" />
                <path d="M31 80 L39 80" stroke="#fff" strokeWidth="4.5" />
                <path d="M121 80 L129 80" stroke={accentStroke} strokeWidth="10" />
                <path d="M121 80 L129 80" stroke="#fff" strokeWidth="4.5" />

                <path d="M56 80 H68 M92 80 H104" stroke={accentStroke} strokeWidth="8" />
                <path d="M56 80 H68 M92 80 H104" stroke="#fff" strokeWidth="3.5" />
                <path d="M80 56 V68 M80 92 V104" stroke={accentStroke} strokeWidth="8" />
                <path d="M80 56 V68 M80 92 V104" stroke="#fff" strokeWidth="3.5" />

                <circle cx="80" cy="80" r="16" stroke={accentStroke} strokeWidth="6" />
                <circle cx="80" cy="80" r="16" stroke="#fff" strokeWidth="2.5" />
              </g>
            </Box>
          </motion.div>

          {cardImageUrl ? (
            <Box
              sx={{
                position: "absolute",
                width: { xs: 28, md: 34 },
                height: { xs: 28, md: 34 },
                borderRadius: "7px",
                boxShadow: `0 0 10px ${accentGlow}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={cardImageUrl}
                alt={selectedCard?.name || "selected card"}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "6px",
                  backgroundColor: "transparent",
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                position: "absolute",
                width: { xs: 12, md: 14 },
                height: { xs: 12, md: 14 },
                borderRadius: "999px",
                backgroundColor: "#fff",
                boxShadow: `0 0 10px ${accentGlow}`,
              }}
            />
          )}
        </Box>

        {playerMoveType === "attack" && !cardImageUrl && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              width: { xs: 42, md: 48 },
              height: { xs: 42, md: 48 },
              transform: "translate(-50%, -50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 48 48"
              sx={{
                width: "100%",
                height: "100%",
                filter: reticleFilter,
              }}
            >
              <g fill="none" strokeLinecap="round">
                <circle cx="24" cy="24" r="6" stroke={accentStroke} strokeWidth="4" />
                <circle cx="24" cy="24" r="6" stroke="#fff" strokeWidth="1.75" />
                <path d="M24 8 V16 M24 32 V40 M8 24 H16 M32 24 H40" stroke={accentStroke} strokeWidth="4" />
                <path d="M24 8 V16 M24 32 V40 M8 24 H16 M32 24 H40" stroke="#fff" strokeWidth="1.75" />
              </g>
            </Box>
          </Box>
        )}
      </motion.div>
    );
  };

  return (
    <Box
      sx={{
        perspective: "1200px",
        perspectiveOrigin: "50% 80%", // tweak this
        transformStyle: "preserve-3d",
        ...styles.playerContainer,
        containerType: "inline-size",
      }}
      onClick={() => {
        if (isBlockedAttackTarget) {
          handleShake();
          return;
        } else {
          onSelect();
          if (
            isTutorial &&
            isCurrentPlayer &&
            (progress?.currentStepIndex === 5 ||
              progress?.currentStepIndex === 9)
          ) {
            console.log(
              "🐸 Player platform clicked — triggering next tutorial popover",
            );

            document.querySelector(".driver-popover-close-btn")?.click(); // Close current

            setTimeout(() => {
              triggerDriverStep("[data-driver='continue-button']", {
                title: "Confirm the Move",
                description: "Click confirm to play your card.",
                side: "top",
                align: "center",
              });
            }, 400);
          }

          if (
            isTutorial &&
            id === "bot_1" &&
            progress?.currentStepIndex === 10
          ) {
            console.log("🗡️ Opponent selected – trigger SpinWheel highlight");
            document.querySelector(".driver-popover-close-btn")?.click();
            setTimeout(() => {
              triggerDriverStep("[data-driver='spin-wheel']", {
                title: "Spin the Wheel",
                description: "Spin to determine the accuracy of your attack.",
                side: "top",
                align: "center",
              });
            }, 400);
          }

          if (
            isTutorial &&
            isCurrentPlayer &&
            progress?.currentStepIndex === 9 // step 9 logic
          ) {
            console.log(
              "🐸 Player platform clicked (step 9) — triggering confirm-button",
            );

            // ❌ Close current popover
            document.querySelector(".driver-popover-close-btn")?.click();

            // ✅ Open new popover after short delay
            setTimeout(() => {
              triggerDriverStep("[data-driver='confirm-button']", {
                title: "Confirm the Move",
                description: "Click Confirm to play your card.",
                side: "top",
                align: "center",
              });
            }, 500);
          }
          if (isTutorial) {
            handleTutorialBattleAdvance();
          }
        }
      }}
    >
      {isSelected && (
        <MotionBox
          variants={bounceVariants}
          animate="selected"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 999,
            pointerEvents: "none",
          }}
        >
          <TargetLockOverlay />
        </MotionBox>
      )}
      <MotionBox
        animate={platformControls}
        initial={false}
        whileHover={{
          transform:
            !character.isDead && !isSelected && "rotateX(0deg) scale(1.05)",
        }}
        style={{
          width: "100%",
          height: "100%",
          transform: character?.isDead
            ? "rotateX(60deg)"
            : playerTurn || isSelected
              ? "rotateX(0deg)"
              : "rotateX(30deg)", // start here
          transformOrigin: "bottom center",
          transformStyle: "preserve-3d",
          backfaceVisibility: "hidden",
        }}
      >
        <MotionBox
          animate={containerControls}
          initial={
            matchPhase === "started"
              ? { opacity: 1, y: 0 }
              : { opacity: 0, y: -18 }
          }
          sx={{
            // ...styles.playerContainer,
            width: "100%",
            height: "100%",
            position: "relative",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: containerBackground,
            filter: playerTurn && "drop-shadow(0 0 8px #fbcf57)",
            borderColor,
            // boxShadow: `10px 7px 1px ${boxShadowColor}`,
          }}
          style={{
            cursor: "pointer",
          }}
        >
          {/* ✨ Traveling light on the border — does not affect content */}
          {playerTurn && (
            <motion.div
              aria-hidden
              style={{
                position: "absolute",
                inset: -2, // snug around the outer edge
                borderRadius: 8, // match your container's radius
                zIndex: 1, // above the main background, below your HUD/UI if needed

                // 🔧 thickness & rotation (CSS vars we animate)
                "--bw": "4px", // border width (make thicker/thinner)
                "--rot": "0deg",

                // Two backgrounds: transparent center + conic sweep at the border box
                background:
                  "linear-gradient(#0000, #0000) padding-box, " +
                  "conic-gradient(from var(--rot), " +
                  "  #ffffffff 0%, #ffffffff 0%, #fff6c2 25%, #ffffffff 37%, #ffffffff 50%, transparent 60%) border-box",

                // Show only the ring band
                WebkitMask:
                  "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",

                // Controls ring thickness without changing layout
                padding: "var(--bw)",
                boxSizing: "border-box",

                // Soft glow
                filter: "drop-shadow(0 0 8px rgba(255,194,34,0.45))",
              }}
              animate={{ ["--rot"]: "360deg" }}
              transition={{
                ["--rot"]: { duration: 1, repeat: Infinity, ease: "linear" },
              }}
            />
          )}

          {/* <Box
        // data-driver={id === "bot_1" ? "opponent-platform" : undefined}
        onClick={() => {
          if (
            playerMoveType === "attack" &&
            ((playerTurn && id === userData?.id) ||
              character?.isDead ||
              character?.cloakedTurns > 0 ||
              (coOp && !isBot) || // PVE: don't attack human teammates
              isTeammate) // TEAM MODE: don't attack same-team targets
          ) {
            handleShake();
            return;
          } else {
            onSelect();
            if (
              isTutorial &&
              isCurrentPlayer &&
              (progress?.currentStepIndex === 5 ||
                progress?.currentStepIndex === 9)
            ) {
              console.log(
                "🐸 Player platform clicked — triggering next tutorial popover"
              );

              document.querySelector(".driver-popover-close-btn")?.click(); // Close current

              setTimeout(() => {
                triggerDriverStep("[data-driver='continue-button']", {
                  title: "Confirm the Move",
                  description: "Click confirm to play your card.",
                  side: "top",
                  align: "center",
                });
              }, 400);
            }

            if (
              isTutorial &&
              id === "bot_1" &&
              progress?.currentStepIndex === 10
            ) {
              console.log("🗡️ Opponent selected – trigger SpinWheel highlight");
              document.querySelector(".driver-popover-close-btn")?.click();
              setTimeout(() => {
                triggerDriverStep("[data-driver='spin-wheel']", {
                  title: "Spin the Wheel",
                  description: "Spin to determine the accuracy of your attack.",
                  side: "top",
                  align: "center",
                });
              }, 400);
            }

            if (
              isTutorial &&
              isCurrentPlayer &&
              progress?.currentStepIndex === 9 // step 9 logic
            ) {
              console.log(
                "🐸 Player platform clicked (step 9) — triggering confirm-button"
              );

              // ❌ Close current popover
              document.querySelector(".driver-popover-close-btn")?.click();

              // ✅ Open new popover after short delay
              setTimeout(() => {
                triggerDriverStep("[data-driver='confirm-button']", {
                  title: "Confirm the Move",
                  description: "Click Confirm to play your card.",
                  side: "top",
                  align: "center",
                });
              }, 500);
            }
            if (isTutorial) {
              handleTutorialBattleAdvance();
            }
          }
        }}
        style={{ overflow: "visible !important" }}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10,
          //   outline: "2px solid cyan", // ✅ debugging
          overflow: "visible",
        }}
      >
      
        {showPowerUp.on && (
          <>
            <PowerUpRisers
              isMobile={isMobile}
              color={showPowerUp.color}
              direction={showPowerUp.direction}
            />
            <PowerUpRisers
              isMobile={isMobile}
              color={showPowerUp.color}
              direction={showPowerUp.direction}
            />
          </>
        )}

        {playerEmote?.url && Date.now() - playerEmote.timestamp < 4500 && (
          <img
            src={playerEmote.url}
            alt="Emote"
            style={{
              position: "absolute",
              top: isMobile ? "-112%" : "-172%",
              left: isMobile ? "-110%" : "-20%",
              width: "40%",
              height: "auto",
              zIndex: 999,
              transform: "skewX(50deg)",
            }}
          />
        )}

        {!character?.isDead ? (
          <>
            {incomingCardImg && (
              <motion.img
                src={incomingCardImg}
                alt="played card"
                initial={{ y: -100, x: -100, opacity: 0, skewX: "50deg" }}
                animate={{ y: 0, x: 0, opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, delay: 2 }}
                style={{
                  position: "absolute",
                  width: "30px",
                  height: "auto",
                  zIndex: 150,
                  top: "0%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  borderRadius: 6,
                  pointerEvents: "none",
                }}
              />
            )}

            <motion.img
              src={character?.imageUrl}
              alt="character"
              animate={controls}
              style={{
                ...styles.playerImage,
                transform: "skewX(50deg)",
                left: window.innerWidth >= 900 ? "-6%" : "-75%",
                width: window.innerWidth >= 900 ? "70px" : "50px",
                height: window.innerWidth >= 900 ? "70px" : "50px",
                // outline: "2px solid limegreen", // ✅ add this
                zIndex: 99, // just in case it’s under the popover
              }}
            />

            {!userData?.completedTutorial &&
              ((progress?.currentStepIndex === 0 && id === "bot_1") ||
                progress?.currentStepIndex === 10 ||
                (isCurrentPlayer &&
                  (progress?.currentStepIndex === 5 ||
                    progress?.currentStepIndex === 9))) && (
                <Box
                  data-driver={
                    id === "bot_1" && progress?.currentStepIndex === 10
                      ? "attackselect-button"
                      : id === "bot_1"
                      ? "opponent-platform"
                      : isCurrentPlayer &&
                        (progress?.currentStepIndex === 5 ||
                          progress?.currentStepIndex === 9)
                      ? "player-platform"
                      : undefined
                  }
                  style={{ overflow: "visible" }}
                  sx={{
                    position: "absolute",
                    top: { xs: "-20px", md: "-65px" },
                    left: { xs: "-20px", md: "-20px" },
                    width: { xs: "45px", md: "100px" },
                    height: { xs: "45px", md: "100px" },
                    transform: "skewX(50deg)",
                    zIndex: 0,
                    backgroundColor: "transparent",
                    pointerEvents: "none",
                    //   outline: "2px solid red", // ✅ debugging
                  }}
                />
              )}

            {animationName && (
              <SpriteAnimator
                ref={spriteRef}
                animationName={animationName}
                frameWidth={64}
                frameHeight={64}
                frameCount={12}
                fps={activeAnimation?.fps || 8}
                loop={true}
                style={[
                  styles.playerImage,
                  {
                    height: 64,
                    width: 64,
                    top: -24,
                    left: -5,
                    zIndex: 101,
                    scale: 0.5,
                  },
                ]}
              />
            )}

            {character?.stance === "defense" && (
              <img
                src={require("../../@shared/assets/icons/sheild.png")}
                alt="defense"
                style={{
                  position: "absolute",

                  top: window.innerWidth >= 900 ? -10 : -2,
                  left: window.innerWidth >= 900 ? "15%" : "-30%",
                  height: window.innerWidth >= 900 ? 40 : 25,
                  width: window.innerWidth >= 900 ? 40 : 25,

                  transform: "skewX(50deg)",
                  zIndex: 100,
                }}
              />
            )}
            <motion.div
              style={{
                ...styles.shadowCircle,
                transform: "skewX(50deg) translateX(-34px) translateY(38px)",
                scale: shadowScale,
              }}
            />
          </>
        ) : (
          <SpriteAnimator
            ref={spriteRef}
            animationName="deadAshPile"
            frameWidth={64}
            frameHeight={64}
            frameCount={12}
            fps={12}
            loop={true}
            onFinish={() => console.log("animation done")}
            scale={window.innerWidth >= 900 ? 1 : 1.5}
            style={{
              height: 64,
              width: 64,
              top: window.innerWidth >= 900 ? "-100%" : "-45%",
              left: window.innerWidth >= 900 ? "10%" : "0%",
              position: "absolute",
              transform: "skewX(50deg)",
            }}
          />
        )}
      </Box> */}

          {character?.isDead ? (
            <Box sx={{ width: "auto", height: "94%", position: "relative" }}>
              {!isMyTurn && (
                <Box
                  sx={{
                    background: "#00000060",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "100%",
                    height: "100%",
                    zIndex: 1,
                    borderRadius: 1,
                  }}
                />
              )}
              <img
                style={{ width: "auto", height: "94%" }}
                src={require("../../@shared/assets/back-of-card.png")}
                alt=""
              />
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <MotionBox
                animate={stanceCardControls}
                sx={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                style={{
                  transformStyle: "preserve-3d",
                  // IMPORTANT: do NOT set backfaceVisibility here (it belongs on faces)
                }}
              >
                {/* FRONT FACE */}
                <Box
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backfaceVisibility: "hidden",
                    WebkitBackfaceVisibility: "hidden",
                    transform: "rotateY(0deg)",
                  }}
                >
                  <CharacterCard
                    {...character}
                    health={displayHealth}
                    attack={displayAttack}
                    defense={displayDefense}
                    accuracy={displayAccuracy}
                    heightPer={92}
                  />
                </Box>

                {/* BACK FACE */}
                <Box
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backfaceVisibility: "hidden",
                    WebkitBackfaceVisibility: "hidden",
                    transform: "rotateY(180deg)",
                  }}
                >
                  <img
                    src={require("../../@shared/assets/back-of-card.png")}
                    alt="Card Back"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      borderRadius: 8,
                    }}
                  />
                </Box>

                {/* overlay stays on top of both */}
                {!isMyTurn && !isSelected && !playerTurn && (
                  <Box
                    sx={{
                      background: "#00000080",
                      position: "absolute",
                      inset: 0,
                      zIndex: 5,
                      borderRadius: 1,
                      pointerEvents: "none",
                    }}
                  />
                )}
              </MotionBox>
            </Box>
          )}
        </MotionBox>
      </MotionBox>
      {character.isDead ? (
        <SpriteAnimator
          ref={spriteRef}
          animationName="deadAshPile"
          frameWidth={64}
          frameHeight={64}
          frameCount={12}
          fps={12}
          loop={true}
          onFinish={() => console.log("animation done")}
          scale={window.innerWidth >= 900 ? 0.75 : 1.5}
          style={{
            // height: 64,
            // width: 64,
            position: "absolute",
            bottom: "0%",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        />
      ) : (
        <>
          <Box
            sx={{
              position: "absolute",
              bottom: "-10%",
              left: "50%",
              transform: "translateX(-50%)",
              height: "30%",
              width: "40%",
              zIndex: 100,
              display: "flex",
              justiftContent: "center",
              alignItems: "center",
              containerType: "inline-size",
            }}
          >
            <MotionDiv
              animate={stanceIconControls}
              sx={{
                width: "100%",
                height: "100%",
                position: "relative",
              }}
            >
              {character?.stance === "defense" ? (
                <img
                  src={require("../../@shared/assets/icons/sheild.png")}
                  alt="defense"
                  style={{
                    width: "100%",
                    height: "100%",
                    filter: "drop-shadow(0 0 8px rgba(255,255,255,0.7))",
                  }}
                />
              ) : (
                <img
                  src={require("../../@shared/assets/icons/swords_icon2.png")}
                  alt="defense"
                  style={{
                    width: "100%",
                    height: "100%",
                    filter: isShownStatBuffed
                      ? character?.stance === "defense"
                        ? "drop-shadow(0 0 8px rgba(255, 213, 79, 1))"
                        : "drop-shadow(0 0 8px rgba(255, 152, 0, 1))"
                      : "drop-shadow(0 0 8px rgba(255, 255, 255, 1))",
                  }}
                />
              )}

              {isShownStatModified && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1, // <-- UNDER the number (z=2), ABOVE the icon
                    pointerEvents: "none",
                    overflow: "visible",
                  }}
                >
                  <PowerUpRisers
                    isMobile={isMobile}
                    color={
                      isShownStatBuffed
                        ? character?.stance === "defense"
                          ? "#ffd54f"
                          : "#ff9800"
                        : "#b66bff"
                    }
                    direction={isShownStatBuffed ? "up" : "down"}
                  />
                </Box>
              )}

              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "50cqw",
                  textAlign: "center",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  borderRadius: 999,
                  color: "#fff",
                  textShadow:
                    "-2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 2px 2px 0 #000",
                  zIndex: 2,
                  whiteSpace: "nowrap",
                }}
              >
                {character?.stance === "attack" ? totalAttack : totalDefense}
              </Typography>
            </MotionDiv>
          </Box>

          <Box
            sx={{
              position: "absolute",
              bottom: "-6%",
              left: "3%",
              height: "20%",
              width: "30%",
              zIndex: 99,
              display: "flex",
              justiftContent: "center",
              alignItems: "center",
              containerType: "inline-size",
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                position: "relative",
              }}
            >
              <img
                src={require("../../@shared/assets/icons/heart.png")}
                alt="defense"
                style={{
                  width: "100%",
                  height: "100%",
                  filter: isHealthBuffed
                    ? "drop-shadow(0 0 8px rgba(255, 79, 79, 1))"
                    : "drop-shadow(0 0 8px rgba(255, 255, 255, 1))",
                }}
              />

              {isHealthBuffed && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1, // <-- UNDER the number (z=2), ABOVE the icon
                    pointerEvents: "none",
                    overflow: "visible",
                  }}
                >
                  <PowerUpRisers
                    isMobile={isMobile}
                    color={"#ff5252"}
                    direction={"up"}
                  />
                </Box>
              )}
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "50cqw",
                  textAlign: "center",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  borderRadius: 999,
                  color: "#fff",
                  textShadow:
                    "-2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 2px 2px 0 #000",
                  zIndex: 2,
                  whiteSpace: "nowrap",
                }}
              >
                {totalHealth}
              </Typography>
            </Box>
          </Box>

          {character?.stance === "attack" && (
            <Box
              sx={{
                position: "absolute",
                bottom: "-6%",
                right: "3%",
                height: "20%",
                width: "30%",
                zIndex: 99,
                display: "flex",
                justiftContent: "center",
                alignItems: "center",
                containerType: "inline-size",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                }}
              >
                <img
                  src={require("../../@shared/assets/icons/cross-hair.png")}
                  alt="defense"
                  style={{
                    width: "100%",
                    height: "100%",
                    filter: isShownStatBuffed
                      ? "drop-shadow(0 0 8px rgba(0, 208, 255, 1))"
                      : "drop-shadow(0 0 8px rgba(255, 255, 255, 1))",
                  }}
                />

                {(isAccuracyDebuffed || isAccuracyBuffed) && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 1, // <-- UNDER the number (z=2), ABOVE the icon
                      pointerEvents: "none",
                      overflow: "visible",
                    }}
                  >
                    <PowerUpRisers
                      isMobile={isMobile}
                      color={isAccuracyBuffed ? "#30d8fc" : "#ff5252"}
                      direction={isAccuracyBuffed ? "up" : "down"}
                    />
                  </Box>
                )}
                <Typography
                  sx={{
                    fontWeight: "bold",
                    fontSize: "50cqw",
                    textAlign: "center",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    borderRadius: 999,
                    color: "#fff",
                    textShadow:
                      "-2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 2px 2px 0 #000",
                    zIndex: 2,
                    whiteSpace: "nowrap",
                  }}
                >
                  {totalAccuracy}
                </Typography>
              </Box>
            </Box>
          )}
        </>
      )}

      {!character?.isDead && elementImg && (
        <Box
          style={{
            position: "absolute",
            top: playerTurn || isSelected ? "-5%" : "10%",
            right: "-10%",
            width: "35%",
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            gap: "6%",
          }}
        >
          <Box
            style={{
              height: "25%",
              width: "100%",
              zIndex: 100,
              borderRadius: 999,
              border: `3px solid ${getElementColor()}`,
              backgroundColor: elementGBColor,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              src={elementImg}
              alt=""
              style={{
                width: "75%",
                height: "auto",
                objectFit: "contain",
              }}
            />
          </Box>

          {statusIndicators.length > 0 && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {statusIndicators.map((status) => (
                <Tooltip
                  key={status.key}
                  title={`${status.label}: ${status.description}`}
                  arrow
                  placement="left"
                >
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {status.kind === "image" ? (
                      <Box
                        component="img"
                        src={status.src}
                        alt={status.label}
                        sx={{
                          width: { xs: 26, md: 32 },
                          height: { xs: 36, md: 44 },
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: { xs: 26, md: 32 },
                          height: { xs: 26, md: 32 },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Cancel
                          sx={{
                            fontSize: { xs: 18, md: 22 },
                            color: "#cf2020",
                          }}
                        />
                      </Box>
                    )}
                    <Typography
                      sx={{
                        position: "absolute",
                        top: -6,
                        right: -8,
                        fontSize: { xs: "0.72rem", md: "0.82rem" },
                        fontWeight: 900,
                        lineHeight: 1,
                        color: "#fff",
                        textShadow:
                          "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
                        pointerEvents: "none",
                      }}
                    >
                      {status.count}
                    </Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          )}
        </Box>
      )}
      <Typography
        sx={{
          width: "84%",
          fontWeight: "bold",
          fontSize: "10cqw",
          textAlign: "center",
          position: "absolute",
          top: character?.isDead
            ? "47%"
            : playerTurn || isSelected
              ? "-5%"
              : "10%",
          left: "42%",
          transform: "translateX(-50%)",
          backgroundColor: containerBackground,
          borderRadius: 999,
          color: isCurrentPlayer || playerTurn ? "black" : "white",
          zIndex: 99,
        }}
      >
        {name}
      </Typography>
      {away && !character?.isDead && (
        <Typography
          sx={{
            position: "absolute",
            top: "19%",
            left: "68%",
            transform: "translateX(-50%)",
            px: 1.1,
            py: 0.15,
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.35)",
            background:
              "linear-gradient(180deg, rgba(255,170,82,0.98) 0%, rgba(255,96,42,0.98) 100%)",
            color: "#1f1308",
            fontWeight: 900,
            fontSize: "5cqw",
            letterSpacing: "0.12em",
            zIndex: 101,
            boxShadow: "0 6px 16px rgba(255, 103, 44, 0.45)",
          }}
        >
          AWAY
        </Typography>
      )}
      {playerEmote?.url && Date.now() - playerEmote.timestamp < 4500 && (
        <img
          src={playerEmote.url}
          alt="Emote"
          style={{
            position: "absolute",
            top: "-30%",
            left: "50%",
            transform: "translateX(-50%)",
            width: "60%",
            height: "auto",
            zIndex: 999,
          }}
        />
      )}
    </Box>
  );
};

const styles = {
  playerContainer: {
    height: { xs: "80%", md: "100%" },
    aspectRatio: "2.5/3.5",
    position: "relative",
    margin: "2px",
    // border: "1px solid #30d8fc",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible",
    //   boxShadow: `
    //   0 12px 25px rgba(0, 0, 0, 0.45),
    //   inset 0 -1px 0 rgba(255, 255, 255, 0.15)
    // `,
  },
  playerImage: {
    width: "70px",
    height: "70px",
    borderRadius: "25px",
    position: "absolute",
    bottom: "60%",
    left: "-6%",
    zIndex: 100,
    transform: "skewX(50deg)",
  },
  shadowCircle: {
    width: "50%",
    height: "20%",
    borderRadius: "50%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    position: "absolute",
    bottom: "25%",
    left: "25%",
    zIndex: 10,
  },
  turnIndicatorText: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#fff",
    transform: [{ skewX: "50deg" }],
    position: "absolute",
    top: -40,
    left: "-60%",
    padding: "2px 6px",
    backgroundColor: "#16116e",
    borderRadius: 4,
    zIndex: 100,
  },
};

export default PlayerPlatform;
